import React from 'react';
import { motion } from 'framer-motion';
import { Box } from '@mui/material';

// Animated Robot/Process Automation Icon
export const ProcessAutomationSVG = ({ size = 80, color = '#f47216' }) => (
  <motion.svg
    width={size}
    height={size}
    viewBox="0 0 100 100"
    initial={{ opacity: 0, scale: 0.8 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.5 }}
  >
    <defs>
      <linearGradient id="robotGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor={color} />
        <stop offset="100%" stopColor="#db1d2b" />
      </linearGradient>
      <filter id="glow">
        <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
        <feMerge>
          <feMergeNode in="coloredBlur"/>
          <feMergeNode in="SourceGraphic"/>
        </feMerge>
      </filter>
    </defs>
    
    {/* Robot head */}
    <motion.rect
      x="25" y="20" width="50" height="40" rx="8"
      fill="url(#robotGrad)"
      filter="url(#glow)"
      animate={{ y: [20, 18, 20] }}
      transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
    />
    
    {/* Eyes */}
    <motion.circle
      cx="38" cy="38" r="6"
      fill="#fff"
      animate={{ scale: [1, 1.1, 1] }}
      transition={{ duration: 1.5, repeat: Infinity }}
    />
    <motion.circle
      cx="62" cy="38" r="6"
      fill="#fff"
      animate={{ scale: [1, 1.1, 1] }}
      transition={{ duration: 1.5, repeat: Infinity, delay: 0.2 }}
    />
    
    {/* Pupils */}
    <motion.circle cx="38" cy="38" r="3" fill="#231f20" />
    <motion.circle cx="62" cy="38" r="3" fill="#231f20" />
    
    {/* Antenna */}
    <motion.line
      x1="50" y1="20" x2="50" y2="10"
      stroke={color}
      strokeWidth="3"
      strokeLinecap="round"
    />
    <motion.circle
      cx="50" cy="8" r="4"
      fill={color}
      animate={{ scale: [1, 1.3, 1], opacity: [0.8, 1, 0.8] }}
      transition={{ duration: 1, repeat: Infinity }}
    />
    
    {/* Body */}
    <motion.rect
      x="30" y="65" width="40" height="25" rx="5"
      fill="url(#robotGrad)"
      animate={{ y: [65, 63, 65] }}
      transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut', delay: 0.1 }}
    />
    
    {/* Gears */}
    <motion.circle
      cx="50" cy="77" r="8"
      fill="none"
      stroke="#fff"
      strokeWidth="2"
      strokeDasharray="4 2"
      animate={{ rotate: 360 }}
      transition={{ duration: 4, repeat: Infinity, ease: 'linear' }}
    />
  </motion.svg>
);

// Animated Document Processing Icon
export const DocumentProcessingSVG = ({ size = 80, color = '#f47216' }) => (
  <motion.svg
    width={size}
    height={size}
    viewBox="0 0 100 100"
    initial={{ opacity: 0, scale: 0.8 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.5 }}
  >
    <defs>
      <linearGradient id="docGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor={color} />
        <stop offset="100%" stopColor="#db1d2b" />
      </linearGradient>
    </defs>
    
    {/* Main document */}
    <motion.path
      d="M25 10 L65 10 L75 20 L75 90 L25 90 Z"
      fill="#fff"
      stroke="url(#docGrad)"
      strokeWidth="3"
      initial={{ pathLength: 0 }}
      animate={{ pathLength: 1 }}
      transition={{ duration: 1.5 }}
    />
    
    {/* Folded corner */}
    <path d="M65 10 L65 20 L75 20" fill="#f8f8f8" stroke="url(#docGrad)" strokeWidth="2" />
    
    {/* Scanning lines */}
    {[30, 45, 60, 75].map((y, i) => (
      <motion.line
        key={i}
        x1="32" y1={y} x2="68" y2={y}
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        initial={{ scaleX: 0 }}
        animate={{ scaleX: [0, 1, 0] }}
        transition={{ 
          duration: 2, 
          repeat: Infinity, 
          delay: i * 0.3,
          ease: 'easeInOut' 
        }}
        style={{ transformOrigin: 'left' }}
      />
    ))}
    
    {/* Scanning beam */}
    <motion.rect
      x="30" y="25" width="40" height="3"
      fill={color}
      opacity="0.5"
      animate={{ y: [25, 80, 25] }}
      transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
    />
    
    {/* Check mark */}
    <motion.path
      d="M55 45 L62 52 L75 35"
      fill="none"
      stroke="#4caf50"
      strokeWidth="4"
      strokeLinecap="round"
      strokeLinejoin="round"
      initial={{ pathLength: 0, opacity: 0 }}
      animate={{ pathLength: 1, opacity: [0, 1, 1, 0] }}
      transition={{ duration: 3, repeat: Infinity, delay: 1.5 }}
    />
  </motion.svg>
);

// Animated Dashboard Icon
export const DashboardSVG = ({ size = 80, color = '#f47216' }) => (
  <motion.svg
    width={size}
    height={size}
    viewBox="0 0 100 100"
    initial={{ opacity: 0, scale: 0.8 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.5 }}
  >
    <defs>
      <linearGradient id="dashGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor={color} />
        <stop offset="100%" stopColor="#db1d2b" />
      </linearGradient>
    </defs>
    
    {/* Screen frame */}
    <rect x="10" y="15" width="80" height="55" rx="5" fill="#231f20" />
    <rect x="15" y="20" width="70" height="45" rx="3" fill="#1a1a2e" />
    
    {/* Chart bars */}
    {[25, 40, 55, 70].map((x, i) => (
      <motion.rect
        key={i}
        x={x}
        y={60}
        width="10"
        height="0"
        rx="2"
        fill="url(#dashGrad)"
        animate={{ height: [0, 20 + i * 8, 20 + i * 8] }}
        transition={{ duration: 1, delay: i * 0.2, repeat: Infinity, repeatDelay: 3 }}
        style={{ transformOrigin: 'bottom' }}
      />
    ))}
    
    {/* Line chart */}
    <motion.path
      d="M20 50 L35 40 L50 45 L65 30 L80 35"
      fill="none"
      stroke="#4caf50"
      strokeWidth="2"
      strokeLinecap="round"
      initial={{ pathLength: 0 }}
      animate={{ pathLength: [0, 1] }}
      transition={{ duration: 2, repeat: Infinity, repeatDelay: 2 }}
    />
    
    {/* Data points */}
    {[{ x: 35, y: 40 }, { x: 50, y: 45 }, { x: 65, y: 30 }, { x: 80, y: 35 }].map((point, i) => (
      <motion.circle
        key={i}
        cx={point.x}
        cy={point.y}
        r="3"
        fill="#4caf50"
        initial={{ scale: 0 }}
        animate={{ scale: [0, 1, 1] }}
        transition={{ duration: 0.5, delay: 0.5 + i * 0.3, repeat: Infinity, repeatDelay: 4 }}
      />
    ))}
    
    {/* Stand */}
    <rect x="40" y="72" width="20" height="5" rx="1" fill="#231f20" />
    <rect x="35" y="77" width="30" height="4" rx="2" fill="#231f20" />
  </motion.svg>
);

// Animated App Development Icon
export const AppDevelopmentSVG = ({ size = 80, color = '#f47216' }) => (
  <motion.svg
    width={size}
    height={size}
    viewBox="0 0 100 100"
    initial={{ opacity: 0, scale: 0.8 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.5 }}
  >
    <defs>
      <linearGradient id="appGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor={color} />
        <stop offset="100%" stopColor="#db1d2b" />
      </linearGradient>
    </defs>
    
    {/* Phone frame */}
    <rect x="30" y="10" width="40" height="80" rx="8" fill="#231f20" />
    <rect x="33" y="18" width="34" height="60" rx="3" fill="#1a1a2e" />
    
    {/* Speaker */}
    <rect x="42" y="13" width="16" height="2" rx="1" fill="#444" />
    
    {/* App icons grid */}
    {[
      { x: 37, y: 25, delay: 0 },
      { x: 52, y: 25, delay: 0.1 },
      { x: 37, y: 42, delay: 0.2 },
      { x: 52, y: 42, delay: 0.3 },
      { x: 37, y: 59, delay: 0.4 },
      { x: 52, y: 59, delay: 0.5 },
    ].map((icon, i) => (
      <motion.rect
        key={i}
        x={icon.x}
        y={icon.y}
        width="12"
        height="12"
        rx="3"
        fill="url(#appGrad)"
        initial={{ scale: 0 }}
        animate={{ scale: [0, 1.1, 1] }}
        transition={{ 
          duration: 0.5, 
          delay: icon.delay,
          repeat: Infinity,
          repeatDelay: 4
        }}
      />
    ))}
    
    {/* Home button/indicator */}
    <motion.rect
      x="42" y="82" width="16" height="4" rx="2"
      fill="#444"
      animate={{ opacity: [0.5, 1, 0.5] }}
      transition={{ duration: 2, repeat: Infinity }}
    />
    
    {/* Code brackets floating */}
    <motion.text
      x="15" y="50"
      fill={color}
      fontSize="16"
      fontFamily="monospace"
      animate={{ y: [50, 45, 50], opacity: [0.5, 1, 0.5] }}
      transition={{ duration: 2, repeat: Infinity }}
    >
      {'</>'}
    </motion.text>
    
    <motion.text
      x="72" y="50"
      fill="#db1d2b"
      fontSize="16"
      fontFamily="monospace"
      animate={{ y: [50, 55, 50], opacity: [0.5, 1, 0.5] }}
      transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
    >
      {'{}'}
    </motion.text>
  </motion.svg>
);

// Animated Consulting Icon
export const ConsultingSVG = ({ size = 80, color = '#f47216' }) => (
  <motion.svg
    width={size}
    height={size}
    viewBox="0 0 100 100"
    initial={{ opacity: 0, scale: 0.8 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.5 }}
  >
    <defs>
      <linearGradient id="consultGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor={color} />
        <stop offset="100%" stopColor="#db1d2b" />
      </linearGradient>
    </defs>
    
    {/* Head/Brain */}
    <motion.circle
      cx="50" cy="35" r="25"
      fill="url(#consultGrad)"
      animate={{ scale: [1, 1.02, 1] }}
      transition={{ duration: 3, repeat: Infinity }}
    />
    
    {/* Brain pattern */}
    <motion.path
      d="M40 30 Q45 25 50 30 Q55 35 60 30 M35 40 Q42 35 50 40 Q58 35 65 40"
      fill="none"
      stroke="#fff"
      strokeWidth="2"
      strokeLinecap="round"
      animate={{ opacity: [0.5, 1, 0.5] }}
      transition={{ duration: 2, repeat: Infinity }}
    />
    
    {/* Lightbulb idea rays */}
    {[0, 45, 90, 135, 180, 225, 270, 315].map((angle, i) => (
      <motion.line
        key={i}
        x1="50"
        y1="35"
        x2={50 + Math.cos(angle * Math.PI / 180) * 35}
        y2={35 + Math.sin(angle * Math.PI / 180) * 35}
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        opacity="0.3"
        animate={{ 
          opacity: [0.1, 0.5, 0.1],
          x2: 50 + Math.cos(angle * Math.PI / 180) * (35 + 5),
          y2: 35 + Math.sin(angle * Math.PI / 180) * (35 + 5),
        }}
        transition={{ 
          duration: 2, 
          repeat: Infinity, 
          delay: i * 0.1 
        }}
      />
    ))}
    
    {/* People icons */}
    <motion.circle cx="25" cy="80" r="8" fill={color} opacity="0.8" />
    <motion.circle cx="50" cy="80" r="8" fill="#db1d2b" opacity="0.8" />
    <motion.circle cx="75" cy="80" r="8" fill={color} opacity="0.8" />
    
    {/* Connection lines */}
    <motion.path
      d="M33 80 L42 80 M58 80 L67 80"
      stroke="#231f20"
      strokeWidth="2"
      strokeDasharray="4 2"
      animate={{ strokeDashoffset: [0, -12] }}
      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
    />
  </motion.svg>
);

// Animated AI Icon
export const AISVG = ({ size = 80, color = '#f47216' }) => (
  <motion.svg
    width={size}
    height={size}
    viewBox="0 0 100 100"
    initial={{ opacity: 0, scale: 0.8 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.5 }}
  >
    <defs>
      <linearGradient id="aiGrad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor={color} />
        <stop offset="100%" stopColor="#db1d2b" />
      </linearGradient>
      <filter id="aiGlow">
        <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
        <feMerge>
          <feMergeNode in="coloredBlur"/>
          <feMergeNode in="SourceGraphic"/>
        </feMerge>
      </filter>
    </defs>
    
    {/* Central brain/chip */}
    <motion.rect
      x="30" y="30" width="40" height="40" rx="8"
      fill="url(#aiGrad)"
      filter="url(#aiGlow)"
      animate={{ scale: [1, 1.05, 1] }}
      transition={{ duration: 2, repeat: Infinity }}
    />
    
    {/* Neural network pattern */}
    <motion.circle cx="42" cy="42" r="4" fill="#fff" opacity="0.8" />
    <motion.circle cx="58" cy="42" r="4" fill="#fff" opacity="0.8" />
    <motion.circle cx="42" cy="58" r="4" fill="#fff" opacity="0.8" />
    <motion.circle cx="58" cy="58" r="4" fill="#fff" opacity="0.8" />
    <motion.circle cx="50" cy="50" r="5" fill="#fff" />
    
    {/* Neural connections */}
    <motion.path
      d="M42 42 L50 50 L58 42 M42 58 L50 50 L58 58"
      stroke="#fff"
      strokeWidth="1.5"
      fill="none"
      animate={{ opacity: [0.5, 1, 0.5] }}
      transition={{ duration: 1.5, repeat: Infinity }}
    />
    
    {/* Connection pins */}
    {[
      { x1: 50, y1: 30, x2: 50, y2: 15 },
      { x1: 50, y1: 70, x2: 50, y2: 85 },
      { x1: 30, y1: 50, x2: 15, y2: 50 },
      { x1: 70, y1: 50, x2: 85, y2: 50 },
    ].map((line, i) => (
      <motion.g key={i}>
        <motion.line
          x1={line.x1}
          y1={line.y1}
          x2={line.x2}
          y2={line.y2}
          stroke={color}
          strokeWidth="3"
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }}
        />
        <motion.circle
          cx={line.x2}
          cy={line.y2}
          r="4"
          fill={color}
          animate={{ scale: [1, 1.3, 1] }}
          transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }}
        />
      </motion.g>
    ))}
    
    {/* Data streams */}
    {[20, 40, 60, 80].map((pos, i) => (
      <motion.circle
        key={`stream-${i}`}
        cx={i % 2 === 0 ? 15 : 85}
        cy={50}
        r="2"
        fill={color}
        animate={{ 
          cx: i % 2 === 0 ? [15, 50] : [85, 50],
          opacity: [0, 1, 0],
        }}
        transition={{ 
          duration: 1.5, 
          repeat: Infinity, 
          delay: i * 0.4 
        }}
      />
    ))}
  </motion.svg>
);

// Animated Stats Card
export const AnimatedStatsCard = ({ children, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.6, delay }}
    whileHover={{ 
      scale: 1.05, 
      y: -10,
      transition: { duration: 0.3 } 
    }}
    style={{ height: '100%' }}
  >
    {children}
  </motion.div>
);

// Animated Section Title
export const AnimatedTitle = ({ children }) => (
  <motion.div
    initial={{ opacity: 0, y: -20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.8 }}
  >
    {children}
  </motion.div>
);

// Staggered fade-in container
export const StaggerContainer = ({ children, staggerDelay = 0.1 }) => (
  <motion.div
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true }}
    variants={{
      hidden: { opacity: 0 },
      visible: {
        opacity: 1,
        transition: {
          staggerChildren: staggerDelay,
        },
      },
    }}
  >
    {children}
  </motion.div>
);

export const StaggerItem = ({ children }) => (
  <motion.div
    variants={{
      hidden: { opacity: 0, y: 20 },
      visible: { opacity: 1, y: 0 },
    }}
    transition={{ duration: 0.5 }}
  >
    {children}
  </motion.div>
);

// Glassmorphism Card wrapper
export const GlassCard = ({ children, sx = {} }) => (
  <Box
    sx={{
      background: 'rgba(255, 255, 255, 0.85)',
      backdropFilter: 'blur(20px)',
      WebkitBackdropFilter: 'blur(20px)',
      borderRadius: '20px',
      border: '1px solid rgba(255, 255, 255, 0.3)',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
      ...sx,
    }}
  >
    {children}
  </Box>
);

export default {
  ProcessAutomationSVG,
  DocumentProcessingSVG,
  DashboardSVG,
  AppDevelopmentSVG,
  ConsultingSVG,
  AISVG,
  AnimatedStatsCard,
  AnimatedTitle,
  StaggerContainer,
  StaggerItem,
  GlassCard,
};


